import React, { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import { Dialog, Button, TextInput, ToggleButtonGroup } from 'react-scale/components/design-system';
import { ScreeningPendingReview } from '@scale/shared/taskerScreening/types';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import { QualificationType } from '@scale/shared/types/qualification/types';
import { EgregiousnessLevel } from '@scale/shared/types/mlQualityMetrics/types';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';
import SanitizedMarkdown from 'react-scale/components/design-system/SanitizedMarkdown';
import { TaskerScreeningInstructionsContents } from 'react-scale/corp/views/rapid/TaskerScreeningUtils/TaskerScreeningInstructionsModal';
import apiFetch from 'react-scale/lib/api-fetch';
import SelectCourse from 'react-scale/corp/components/SelectCourse';
import SelectQualification from 'react-scale/corp/components/SelectQualification';
import {
  questionPlaceholder,
  answerPlaceholder,
} from 'react-scale/corp/views/rapid/TaskerScreeningUtils/TaskerScreeningBenchmarkCreationTool';
import TextCollectionDiff from 'react-scale/components/scalers/tasks/textcollection/TextCollectionDiff';
import withV2Timer from 'react-scale/components/scalers/withV2Timer';
import { Typography, Tag, Types, Tooltip, Spinner, TextArea } from '@scale/scaleui';
import API from '@scale/frontend-lib/dashboard-api';
import { WorkerSkillDimension } from '@scale/shared-types';
import { QualificationSelectOption } from '@scale/shared/types/params/course';

const EgregiousnessLevelToVariant: Record<EgregiousnessLevel, Types.Variant> = {
  [EgregiousnessLevel.Egregious]: 'danger',
  [EgregiousnessLevel.Suspect]: 'warning',
  [EgregiousnessLevel.Acceptable]: 'success',
};

interface SelectOption {
  label: string;
  value: string;
  name?: string;
}

interface MlSignalResult {
  questionScore: number;
  questionStatus: Types.Variant;
  name: string;
  answerScore: number;
  answerStatus: Types.Variant;
}

interface TaskerScreeningGradingReviewModalProps {
  review: ScreeningPendingReview;
  open: boolean;
  setOpen: (open: boolean) => void;
  updateReviewScore: (props: { score: number; feedback?: string }) => void;

  // benchmark creation
  setReview?: (review: ScreeningPendingReview) => void;
  creatingBenchmark?: boolean;
  expectedScore?: number;
  publishBenchmark?: () => void;

  // for timer
  _id?: string;
  loggedUserId?: string;
}

// eslint-disable-next-line complexity
const TaskerScreeningGradingReviewModal: React.FC<TaskerScreeningGradingReviewModalProps> = ({
  review,
  open,
  setOpen,
  updateReviewScore,
  creatingBenchmark,
  setReview,
  expectedScore,
  publishBenchmark,
}) => {
  const [showDescription, setShowDescription] = useState(false);
  const [currentSkillInstructionsUrl, setCurrentSkillInstructionsUrl] = useState<string>();
  const [showInstructions, setShowInstructions] = useState(false);
  const [showDiff, setShowDiff] = useState(true); // for rewrites

  const [showAllMlSignals, setShowAllMlSignals] = useState<boolean>(false);
  const [mlSignalLoading, setMlSignalLoading] = useState<boolean>(false);
  const [mlSignalResults, setMlSignalResults] = useState<MlSignalResult[][]>([]);
  const hasMLSignals =
    !review.isRewrite &&
    review.metadata &&
    review.metadata.qualityScores &&
    review.metadata.qualityEgregiousnessLevel;

  const [feedback, setFeedback] = useState<string>();

  // benchmark creation
  const [isEditing, setIsEditing] = useState<boolean>(!!creatingBenchmark);
  const [courseSelect, setCourseSelect] = useState<SelectOption>({
    label: review.course,
    value: review.course,
  });
  const [qualificationOption, setQualificationOption] = useState<QualificationSelectOption>({
    qualificationType: QualificationType.WorkerSkill,
    workerSkill: {
      dimension: review.workerSkillDimension ?? '',
      label: review.workerSkillName ?? '',
      value: review.workerSkillId ?? '',
    },
  });

  const handleUpdateWorkerSkill = useCallback(
    (value: any) => {
      const updatedQualOption = qualificationOption;
      if (Object.values(WorkerSkillDimension).includes(value)) {
        if (!updatedQualOption.workerSkill) {
          updatedQualOption.workerSkill = {
            label: '',
            value: '',
            dimension: value,
          };
        } else {
          updatedQualOption.workerSkill.dimension = value;
        }
      } else {
        updatedQualOption.workerSkill = {
          ...updatedQualOption.workerSkill,
          ...value,
        };
      }
      setQualificationOption({ ...updatedQualOption });
      if (updatedQualOption && setReview) {
        setReview({
          ...review,
          workerSkillName: updatedQualOption.workerSkill?.label ?? '',
          workerSkillId: updatedQualOption.workerSkill?.value ?? '',
          workerSkillDimension:
            updatedQualOption.workerSkill?.dimension ?? WorkerSkillDimension.Overall,
        });
      }
    },
    [qualificationOption, setQualificationOption, setReview, review],
  );

  const thresholdToVariantHelper = (
    threshold: {
      egregiousThreshold: number;
      suspectThreshold: number;
    },
    value: number,
  ): Types.Variant => {
    if (value <= threshold.egregiousThreshold) return 'danger';
    if (value <= threshold.suspectThreshold) return 'warning';
    return 'success';
  };

  // Set the thresholds into state
  useEffect(() => {
    const fetchThresholdsAndSetTags = async () => {
      const { course } = await API.labeling.getCourseInfo(review.course);
      const section = course.sections.filter((s: any) => s.title === review.sectionTitle);
      if (section.length > 0) {
        const mlConfigs = section[0].contents.mlQualitySignalsConfigs ?? [];
        const thresholds = mlConfigs.map(
          (config: Record<string, any>) => config.egregiousThresholds ?? {},
        );
        const mlQualitySignals = review.metadata?.qualityScores;
        const signals = [];

        for (const [index, question] of mlQualitySignals.entries()) {
          const temp = [];
          for (const key of Object.keys(question)) {
            if (!_.isEmpty(thresholds[index][key])) {
              const name = key.split('-')[0];
              temp.push({
                name,
                questionStatus: thresholdToVariantHelper(
                  thresholds[index][key].promptThresholds,
                  question[key].promptQualityScore,
                ),
                questionScore: question[key].promptQualityScore,
                answerStatus: thresholdToVariantHelper(
                  thresholds[index][key].completionThresholds,
                  question[key].completionQualityScore,
                ),
                answerScore: question[key].completionQualityScore,
              });
            }
          }
          signals.push(temp);
        }
        setMlSignalResults(signals);
        setMlSignalLoading(false);
      }
    };
    if (hasMLSignals) {
      setMlSignalLoading(true);
      fetchThresholdsAndSetTags();
    }
  }, [review, hasMLSignals]);

  const fetchQualificationInstructionsUrl = useCallback(
    async (workerSkillId: string, workerSkillDimension: string) => {
      try {
        const query = {
          workerSkillId,
          workerSkillDimension,
          qualificationType: QualificationType.WorkerSkill,
        };
        const { qualifications: fetchedQualifications } = await apiFetch(
          null,
          `corp-api/find/qualifications`,
          { query },
        );
        setCurrentSkillInstructionsUrl(fetchedQualifications[0].instructionsUrl);
      } catch (error) {
        // do nothing
      }
    },
    [],
  );

  useEffect(() => {
    if (review.workerSkillId) {
      fetchQualificationInstructionsUrl(
        review.workerSkillId,
        review.workerSkillDimension ?? WorkerSkillDimension.Overall,
      );
    }
  }, [fetchQualificationInstructionsUrl, open, review.workerSkillId, review.workerSkillDimension]);

  const gradeOptions: Record<number, string> = {
    0: 'Terrible',
    250: 'Bad',
    500: 'Okay',
    750: 'Good',
    1000: 'Perfect',
  };
  const gradeOptionColors: Record<number, string> = {
    0: '#ffcbc7',
    250: '#ffdfc7',
    500: '#fff8c7',
    750: '#ecffc7',
    1000: '#d1ffc7',
  };
  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={() => {
        setOpen(false);
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-around',
          overflow: 'hidden',
        }}
      >
        <div
          className="banner"
          style={{
            position: 'sticky',
            padding: 16,
            top: 0,
            overflow: 'auto',
            borderBottom: '1px solid lightgray',
            minHeight: '10vh',
            maxHeight: '40vh',
          }}
        >
          {creatingBenchmark && (
            <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 8 }}>
              <Button
                type="secondary"
                style={{ marginRight: 8 }}
                onClick={() => setIsEditing(!isEditing)}
              >
                {`${isEditing ? 'Preview' : 'Edit'} Benchmark`}
              </Button>
              {!isEditing && (
                <Button
                  type="primary"
                  disabled={!_.isNumber(expectedScore)}
                  onClick={publishBenchmark}
                >
                  {!_.isNumber(expectedScore)
                    ? 'Select Expected Score to Publish'
                    : 'Publish Benchmark'}
                </Button>
              )}
            </div>
          )}
          {isEditing ? (
            <div>
              <div
                style={{
                  marginBottom: 16,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'start',
                }}
              >
                <SmartLink type={INamedObjectTypes.Course} id={review.course} />
                <div style={{ width: 400 }}>
                  <SelectCourse
                    value={courseSelect}
                    onChange={courseSelect => {
                      setCourseSelect(courseSelect);
                      if (courseSelect && setReview) {
                        setReview({
                          ...review,
                          course: courseSelect.value,
                        });
                      }
                    }}
                  />
                </div>
                <div style={{ width: 400, paddingLeft: 16 }}>
                  <SelectQualification
                    value={qualificationOption}
                    onWorkerSkillChange={handleUpdateWorkerSkill}
                    qualificationType={QualificationType.WorkerSkill}
                    requireStatus={false}
                  />
                </div>
              </div>

              <TextInput
                value={review.sectionTitle}
                onChange={(e: any) => {
                  if (setReview) {
                    setReview({
                      ...review,
                      sectionTitle: e.target.value,
                    });
                  }
                }}
                label="Section Title"
                fullWidth
                size="large"
                style={{ marginBottom: 16 }}
              />
              <TextInput
                value={review.description}
                onChange={(e: any) => {
                  if (setReview) {
                    setReview({
                      ...review,
                      description: e.target.value,
                    });
                  }
                }}
                label="Description (Markdown)"
                fullWidth
                multiline
                size="large"
              />
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <SmartLink type={INamedObjectTypes.Course} id={review.course} />
                <p>Section: {review.sectionTitle}</p>
                <p style={{ marginLeft: 'auto' }}>ID: {review._id}</p>
              </div>
              {(review.description ?? '').length > 0 && (
                <div>
                  <a onClick={() => setShowDescription(!showDescription)}>
                    {showDescription ? 'Hide' : 'Show'} Section Description
                  </a>
                  {showDescription && (
                    <div>
                      <SanitizedMarkdown text={review.description ?? 'No description.'} />
                    </div>
                  )}
                </div>
              )}
              {currentSkillInstructionsUrl && (
                <div style={{ marginTop: 4, marginBottom: 4 }}>
                  <a onClick={() => setShowInstructions(!showInstructions)}>
                    {showInstructions ? 'Hide' : 'Show'} Auditing Instructions
                  </a>
                  {showInstructions && (
                    <div>
                      <TaskerScreeningInstructionsContents
                        open
                        onClose={() => {}}
                        workerSkillName={review.workerSkillName ?? ''}
                        instructionsUrl={currentSkillInstructionsUrl}
                      />
                    </div>
                  )}
                </div>
              )}
              {hasMLSignals && (
                <div style={{ marginTop: 4, marginBottom: 4 }}>
                  <a onClick={() => setShowAllMlSignals(!showAllMlSignals)}>
                    {showAllMlSignals ? 'Hide Passing' : 'Show All'} ML Signals
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="content scaleui scale-theme" style={{ padding: 16, overflowY: 'auto' }}>
          {isEditing && (
            <ToggleButtonGroup
              value={!!review.isRewrite}
              onChange={(value: boolean | null) => {
                if (setReview) {
                  setReview({
                    ...review,
                    isRewrite: !!value,
                  });
                }
              }}
              options={[
                { value: false, label: 'Text Response' },
                { value: true, label: 'Text Response Rewrite' },
              ]}
            />
          )}
          <div className="flex gap-4 mb-4">
            <Typography.H3>Questions and Answers</Typography.H3>
            {hasMLSignals && (
              <Tooltip
                asChild={false}
                icon={['fas', 'info-circle']}
                text="Grades are based off of ML signals."
                delayDuration={0}
                target={
                  <Tag
                    className="pointer-events-auto cursor-pointer"
                    size="lg"
                    variant={
                      EgregiousnessLevelToVariant[
                        review.metadata?.qualityEgregiousnessLevel as EgregiousnessLevel
                      ]
                    }
                  >
                    {review.metadata?.qualityEgregiousnessLevel}
                  </Tag>
                }
              />
            )}
          </div>
          {isEditing ? (
            <div>
              {review.questions.map((q, i) => (
                <div key={i}>
                  <TextInput
                    value={review.questions[i]}
                    onChange={(e: any) => {
                      const questions = [...review.questions];
                      questions[i] = e.target.value;
                      if (setReview) {
                        setReview({
                          ...review,
                          questions,
                        });
                      }
                    }}
                    label={`Question ${i + 1}`}
                    fullWidth
                    multiline
                    size="large"
                    style={{ marginBottom: 8 }}
                  />
                  <TextInput
                    value={review.answers[i]}
                    onChange={(e: any) => {
                      const answers = [...review.answers];
                      answers[i] = e.target.value;
                      if (setReview) {
                        setReview({
                          ...review,
                          answers,
                        });
                      }
                    }}
                    label={`Answer ${i + 1}`}
                    fullWidth
                    multiline
                    size="large"
                    style={{ marginBottom: 8 }}
                  />
                  {review.isRewrite &&
                    review.rewrittenOriginalAnswers &&
                    review.rewrittenOriginalAnswers?.length > i && (
                      <TextInput
                        value={review.rewrittenOriginalAnswers[i]}
                        onChange={(e: any) => {
                          const rewrittenOriginalAnswers = [
                            ...(review.rewrittenOriginalAnswers ?? []),
                          ];
                          rewrittenOriginalAnswers[i] = e.target.value;
                          if (setReview) {
                            setReview({
                              ...review,
                              rewrittenOriginalAnswers,
                            });
                          }
                        }}
                        label={`Original Answer ${i + 1}`}
                        fullWidth
                        multiline
                        size="large"
                        style={{ marginBottom: 8 }}
                      />
                    )}
                  <Button
                    style={{ marginBottom: 16 }}
                    onClick={() => {
                      const questions = [...review.questions];
                      questions.splice(i, 1);
                      const answers = [...review.answers];
                      answers.splice(i, 1);
                      const rewrittenOriginalAnswers = [...(review.rewrittenOriginalAnswers ?? [])];
                      rewrittenOriginalAnswers.splice(i, 1);
                      if (setReview) {
                        setReview({
                          ...review,
                          questions,
                          answers,
                          rewrittenOriginalAnswers,
                        });
                      }
                    }}
                  >
                    Delete QnA {i + 1}
                  </Button>
                </div>
              ))}
              <Button
                style={{ marginBottom: 16 }}
                onClick={() => {
                  const questions = [...review.questions, questionPlaceholder];
                  const answers = [...review.answers, answerPlaceholder];
                  const rewrittenOriginalAnswers = [
                    ...(review.rewrittenOriginalAnswers ?? []),
                    answerPlaceholder,
                  ];
                  if (setReview) {
                    setReview({
                      ...review,
                      questions,
                      answers,
                      rewrittenOriginalAnswers,
                    });
                  }
                }}
              >
                Add QnA
              </Button>
            </div>
          ) : (
            review.questions.map((q, i) => (
              <div key={q}>
                <div className="flex gap-2 items-center">
                  <Typography.H5>Question</Typography.H5>
                  {hasMLSignals &&
                    (mlSignalResults.length > 0 && !mlSignalLoading ? (
                      mlSignalResults[i]
                        .filter(signal => showAllMlSignals || signal.questionStatus !== 'success')
                        .map(signal => (
                          <Tooltip
                            asChild={false}
                            key={signal.name}
                            text={`Score: ${signal.questionScore.toString()}`}
                            delayDuration={0}
                            target={
                              <Tag
                                className="pointer-events-auto cursor-pointer"
                                size="sm"
                                key={signal.name}
                                variant={signal.questionStatus}
                              >
                                {signal.name}
                              </Tag>
                            }
                          />
                        ))
                    ) : (
                      <Spinner size="sm" />
                    ))}
                </div>
                {
                  <SanitizedMarkdown
                    text={q ?? 'Click "Show Section Description" to see the question.'}
                  />
                }
                <div className="flex gap-2 items-center">
                  <Typography.H5>Answer</Typography.H5>
                  {hasMLSignals &&
                    (mlSignalResults.length > 0 && !mlSignalLoading ? (
                      mlSignalResults[i]
                        .filter(signal => showAllMlSignals || signal.answerStatus !== 'success')
                        .map(signal => (
                          <Tooltip
                            asChild={false}
                            key={signal.name}
                            text={`Score: ${signal.answerScore.toString()}`}
                            delayDuration={0}
                            target={
                              <Tag
                                className="pointer-events-auto cursor-pointer"
                                size="sm"
                                key={signal.name}
                                variant={signal.answerStatus}
                              >
                                {signal.name}
                              </Tag>
                            }
                          />
                        ))
                    ) : (
                      <Spinner size="sm" />
                    ))}
                </div>
                {review.isRewrite && review.rewrittenOriginalAnswers?.length ? (
                  <div>
                    <a onClick={() => setShowDiff(!showDiff)}>
                      Show {showDiff ? 'Only Answer' : 'Answer Diff'}
                    </a>
                    {showDiff ? (
                      <TextCollectionDiff
                        fullWidth
                        originalText={review.rewrittenOriginalAnswers[i]}
                        newText={review.answers[i]}
                      />
                    ) : (
                      <SanitizedMarkdown text={review.answers[i] ?? 'n/a'} />
                    )}
                  </div>
                ) : (
                  <SanitizedMarkdown text={review.answers[i] ?? 'n/a'} />
                )}
              </div>
            ))
          )}
          <TextArea
            id="feedback"
            placeholder="Write feedback for the worker..."
            value={feedback ?? ''}
            rows={2}
            onChange={e => {
              if (setFeedback) {
                setFeedback(e.target.value);
              }
            }}
          />
          <div
            style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: 16 }}
          >
            {creatingBenchmark && <p style={{ marginRight: 8 }}>Expected score:</p>}
            {Object.keys(gradeOptions).map(grade => (
              <Button
                style={{
                  marginRight: 8,
                  background: gradeOptionColors[Number(grade)],
                  border: expectedScore === Number(grade) ? '4px solid lightgray' : '',
                }}
                key={gradeOptions[Number(grade)]}
                onClick={() => updateReviewScore({ score: Number(grade), feedback })}
              >
                {`${gradeOptions[Number(grade)]} (${grade})`}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default withV2Timer(TaskerScreeningGradingReviewModal);
